import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { SwalService } from '@shared/services/swal.service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { UPDATE_PRODUCT, CREATE_PRODUCT, LIST_ALL_PRODUCTS } from '../products.graphql';
import { UpdateProductResponse, CreateProductResponse, ListAllProductsResponse } from '../types/product';
import { ProductActions } from './product.actions';

@Injectable()
export class ProductEffects {
  private readonly actions$ = inject(Actions);
  private readonly graphqlService = inject(GraphQlService);
  private readonly swalService = inject(SwalService);

  loadAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.loadAll),
      switchMap(() =>
        this.graphqlService
          .query<ListAllProductsResponse>({
            query: LIST_ALL_PRODUCTS,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) =>
              ProductActions.loadAllSuccess({
                products: response.data?.listAllProducts?.data ?? [],
              })
            ),
            catchError((error) => of(ProductActions.loadAllFailure({ error })))
          )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.create),
      mergeMap(({ productName, minCapacityMt, minCargoCapacityMt, isBps, allowedMix, colorCode, productCode, productCategoryUuid }) =>
        this.graphqlService
          .mutate<CreateProductResponse>({
            mutation: CREATE_PRODUCT,
            apolloNamespace: ApolloNamespace.registration,
            variables: { productName, minCapacityMt, minCargoCapacityMt, isBps, allowedMix, colorCode, productCode, productCategoryUuid },
          })
          .pipe(
            map((response) => {
              const res = response.data?.createProduct;
              if (res?.status && res.data) {
                return ProductActions.createSuccess({ entity: res.data });
              }

              return ProductActions.createFailure({
                error: res?.message ?? 'Failed to create product',
              });
            }),
            catchError((error) => of(ProductActions.createFailure({ error })))
          )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.update),
      mergeMap(({ productUuId, productName, minCapacityMt, minCargoCapacityMt, isBps, allowedMix, colorCode, productCode, productCategoryUuid }) =>
        this.graphqlService
          .mutate<UpdateProductResponse>({
            mutation: UPDATE_PRODUCT,
            apolloNamespace: ApolloNamespace.registration,
            variables: { productUuId, productName, minCapacityMt, minCargoCapacityMt, isBps, allowedMix, colorCode, productCode, productCategoryUuid },
          })
          .pipe(
            map((response) => {
              const res = response.data?.updateProduct;
              if (res?.status && res.data) {
                return ProductActions.updateSuccess({ entity: res.data });
              }

              return ProductActions.updateFailure({
                error: res?.message ?? 'Failed to update product',
              });
            }),
            catchError((error) => of(ProductActions.updateFailure({ error })))
          )
      )
    )
  );

  notifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProductActions.loadAllFailure,
          ProductActions.createSuccess,
          ProductActions.createFailure,
          ProductActions.updateSuccess,
          ProductActions.updateFailure
        ),
        tap((action) => {
          if (action.type === ProductActions.createSuccess.type) {
            this.swalService.successToast('Product created successfully');
          } else if (action.type === ProductActions.updateSuccess.type) {
            this.swalService.successToast('Product updated successfully');
          } else if (
            action.type === ProductActions.createFailure.type || 
            action.type === ProductActions.updateFailure.type
          ) {
            const anyAction: any = action;
            this.swalService.errorToast(anyAction.error?.toString?.() ?? 'Operation failed');
          } else if (action.type === ProductActions.loadAllFailure.type) {
            this.swalService.errorToast('Failed to load products');
          }
        })
      ),
    { dispatch: false }
  );
}
